/**
 * CARROSSEL — 5 sinais de que seu marketing não é um sistema
 * Perfil: @guilhermeribeiro.ao
 * Formato: 720×900 (4:5) | 30fps | 10s/slide | SEM ÁUDIO
 * Estilo: Editorial Tech News (serif, dark/light dual-mode, accent #E84545)
 */

import React, {useState} from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  spring,
  interpolate,
  Easing,
  Sequence,
  Img,
  staticFile,
} from "remotion";
import {CLAMP} from "../presets/idv";

// ── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  dark:  { bg: "#1A1D26", headline: "#FFFFFF", body: "#C8C4C0", muted: "#888888" },
  light: { bg: "#F7F6F2", headline: "#1A1A1A", body: "#4A4A4A", muted: "#999999" },
  accent: "#E84545",
  accentBg: "rgba(232,69,69,0.15)",
  term: { bg: "#0D1117", border: "rgba(255,255,255,0.08)", prompt: "#4ADE80", error: "#F87171", text: "#C9D1D9", comment: "#8B949E" },
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_MONO = "'JetBrains Mono', 'Fira Code', monospace";
const FONT_SANS = "'Inter', system-ui, sans-serif";
const FONT_CURSIVE = "'Dancing Script', cursive";

const SZ = { top: 70, sides: 50, bottom: 80, footerY: 830 };
const CW = 720 - SZ.sides * 2; // 620

// ── SPRING HELPERS ──────────────────────────────────────────────────────────
const ft = (f: number) => f; // alias
const fadeIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0, 1], { extrapolateRight: CLAMP });
const slideUp = (frame: number, start: number, end: number, dist = 20) =>
  interpolate(frame, [start, end], [dist, 0], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });
const scaleIn = (frame: number, start: number, end: number) =>
  interpolate(frame, [start, end], [0.85, 1], { extrapolateRight: CLAMP, easing: Easing.out(Easing.ease) });

// ── SHARED COMPONENTS ──────────────────────────────────────────────────────
const Footer: React.FC<{mode: "dark" | "light"; handle: string; author: string}> = ({mode, handle, author}) => {
  const m = mode === "dark" ? T.dark : T.light;
  return (
    <div style={{
      position: "absolute",
      bottom: 40,
      left: SZ.sides,
      right: SZ.sides,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 15, fontWeight: 400, color: m.muted}}>{handle}</span>
      <span style={{fontFamily: FONT_CURSIVE, fontSize: 18, fontWeight: 300, color: m.muted}}>{author}</span>
    </div>
  );
};

const SeriesLabel: React.FC<{mode: "dark" | "light"; text: string}> = ({mode, text}) => {
  const frame = useCurrentFrame();
  const m = mode === "dark" ? T.dark : T.light;
  return (
    <div style={{
      position: "absolute",
      top: 30,
      left: SZ.sides,
      opacity: fadeIn(frame, 0, 10),
      fontFamily: FONT_MONO,
      fontSize: 13,
      fontWeight: 500,
      letterSpacing: 2,
      textTransform: "uppercase",
      color: m.muted,
    }}>
      {text}
    </div>
  );
};

const Badge: React.FC<{text: string; delay?: number}> = ({text, delay = 5}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateX(${interpolate(frame, [delay, delay + 15], [-30, 0], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)})}px)`,
      background: T.accent,
      borderRadius: 4,
      padding: "6px 12px",
      display: "inline-flex",
      alignItems: "center",
      marginBottom: 16,
    }}>
      <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#FFFFFF"}}>
        {text}
      </span>
    </div>
  );
};

const NumberHero: React.FC<{value: string; delay?: number}> = ({value, delay = 0}) => {
  const frame = useCurrentFrame();
  const glow = 20 + Math.sin(frame * 0.05) * 8;
  return (
    <div style={{marginBottom: 16}}>
      <div style={{
        opacity: fadeIn(frame, delay, delay + 15),
        transform: `scale(${scaleIn(frame, delay, delay + 10)})`,
        fontFamily: FONT_SERIF,
        fontSize: 88,
        fontWeight: 800,
        color: T.accent,
        lineHeight: 1,
        textShadow: `0 0 ${glow}px rgba(232,69,69,0.3)`,
      }}>
        {value}
      </div>
      <div style={{
        width: interpolate(frame, [delay + 12, delay + 22], [0, 40], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)}),
        height: 3,
        background: T.accent,
        marginTop: 12,
      }} />
    </div>
  );
};

const Headline: React.FC<{children: React.ReactNode; size?: number; color: string; delay?: number}> =
  ({children, size = 48, color, delay = 0}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF,
      fontSize: size,
      fontWeight: 700,
      lineHeight: 1.15,
      color,
      maxWidth: CW,
    }}>
      {children}
    </div>
  );
};

const Body: React.FC<{children: React.ReactNode; color: string; delay?: number}> = ({children, color, delay = 15}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 15),
      transform: `translateY(${slideUp(frame, delay, delay + 15)})`,
      fontFamily: FONT_SERIF,
      fontSize: 22,
      fontWeight: 400,
      lineHeight: 1.4,
      color,
      maxWidth: CW,
    }}>
      {children}
    </div>
  );
};

const Credit: React.FC<{text: string; mode: "dark" | "light"; delay?: number}> = ({text, mode, delay = 35}) => {
  const frame = useCurrentFrame();
  const m = mode === "dark" ? T.dark : T.light;
  return (
    <div style={{
      opacity: fadeIn(frame, delay, delay + 10) * 0.7,
      fontFamily: FONT_MONO,
      fontSize: 12,
      fontWeight: 400,
      color: m.muted,
      marginTop: 16,
    }}>
      {text}
    </div>
  );
};

const Divider: React.FC<{mode: "dark" | "light"; delay?: number}> = ({mode, delay = 30}) => {
  const frame = useCurrentFrame();
  const m = mode === "dark" ? T.dark : T.light;
  return (
    <div style={{
      width: 40,
      height: 2,
      background: mode === "dark" ? "#444" : "#CCC",
      marginTop: 16,
      marginBottom: 16,
      opacity: fadeIn(frame, delay, delay + 10),
    }} />
  );
};

const Checklist: React.FC<{items: string[]; mode: "dark" | "light"; accentColor?: string; delay?: number}> =
  ({items, mode, accentColor = T.accent, delay = 20}) => {
  const frame = useCurrentFrame();
  const m = mode === "dark" ? T.dark : T.light;
  const textColor = mode === "dark" ? "#FFFFFF" : T.light.headline;
  return (
    <div style={{display: "flex", flexDirection: "column", gap: 14, marginTop: 8}}>
      {items.map((item, i) => {
        const fd = delay + i * 8;
        const s = spring({frame: frame - fd, fps: 30, config: {damping: 12, stiffness: 180}});
        const txtFade = fadeIn(frame, fd + 2, fd + 10);
        const txtX = interpolate(frame, [fd + 2, fd + 10], [15, 0], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)});
        const bulletScale = interpolate(frame, [fd, fd + 6, fd + 10], [0, 1.15, 1], {extrapolateRight: CLAMP});
        const checkFade = fadeIn(frame, fd + 4, fd + 8);
        return (
          <div key={i} style={{display: "flex", alignItems: "center", gap: 12, opacity: s}}>
            <div style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: `scale(${bulletScale})`,
              flexShrink: 0,
            }}>
              <span style={{fontSize: 11, color: "#fff", fontWeight: 700}}>✓</span>
            </div>
            <div style={{
              opacity: txtFade,
              transform: `translateX(${txtX}px)`,
              fontFamily: FONT_SERIF,
              fontSize: 21,
              fontWeight: 600,
              lineHeight: 1.3,
              color: textColor,
            }}>
              {item}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TerminalCard: React.FC<{
  label: string;
  lines: {type: "$" | "#" | "text" | "arrow" | "error" | "success"; content: string}[];
  delay?: number;
}> = ({label, lines, delay = 20}) => {
  const frame = useCurrentFrame();
  const opacity = fadeIn(frame, delay, delay + 15);
  const ty = slideUp(frame, delay, delay + 15, 40);
  return (
    <div style={{
      opacity,
      transform: `translateY(${ty})`,
      background: T.term.bg,
      border: `1px solid ${T.term.border}`,
      borderRadius: 12,
      padding: "0 0 20px 0",
      marginTop: 16,
      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      maxWidth: CW,
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        padding: "10px 16px",
        borderBottom: `1px solid ${T.term.border}`,
        gap: 8,
      }}>
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#FF5F57"}} />
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#FEBC2E"}} />
        <div style={{width: 12, height: 12, borderRadius: "50%", background: "#28C840"}} />
        <div style={{flex: 1}} />
        <span style={{fontFamily: FONT_MONO, fontSize: 12, color: T.term.comment}}>{label}</span>
      </div>
      {/* Lines */}
      <div style={{padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8}}>
        {lines.map((line, i) => {
          const color = line.type === "$" ? T.term.prompt
            : line.type === "#" ? T.term.comment
            : line.type === "error" ? T.term.error
            : line.type === "success" ? T.term.prompt
            : line.type === "arrow" ? "#60A5FA"
            : T.term.text;
          const prefix = line.type === "$" ? "$ " : line.type === "#" ? "# " : line.type === "arrow" ? "→ " : "";
          const ld = delay + 15 + i * 8;
          const lo = fadeIn(frame, ld, ld + 6);
          return (
            <div key={i} style={{opacity: lo, fontFamily: FONT_MONO, fontSize: 15, lineHeight: 2, color}}>
              {prefix}{line.content}
            </div>
          );
        })}
        {/* Blinking cursor */}
        <div style={{
          opacity: Math.round(Math.sin(frame * 0.15) * 0.5 + 0.5),
          fontFamily: FONT_MONO,
          fontSize: 15,
          color: T.term.prompt,
        }}>█</div>
      </div>
    </div>
  );
};

const HighlightText: React.FC<{children: React.ReactNode; color: string}> = ({children, color}) => {
  const frame = useCurrentFrame();
  return (
    <span style={{
      background: T.accentBg,
      padding: "2px 6px",
      borderRadius: 3,
    }}>
      {children}
    </span>
  );
};

const SlideNumber: React.FC<{n: string}> = ({n}) => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      position: "absolute",
      bottom: 60,
      right: SZ.sides,
      fontFamily: FONT_SERIF,
      fontSize: 120,
      fontWeight: 800,
      color: "#FFFFFF",
      opacity: interpolate(frame, [10, 25], [0, 0.06], {extrapolateRight: CLAMP}),
      lineHeight: 1,
      userSelect: "none",
    }}>
      {n}
    </div>
  );
};

const GridPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = 0.03 + Math.sin(frame * 0.02) * 0.005;
  return (
    <div style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: "40px 40px",
      pointerEvents: "none",
    }} />
  );
};

const DashboardCard: React.FC<{delay?: number}> = ({delay = 20}) => {
  const frame = useCurrentFrame();
  const opacity = fadeIn(frame, delay, delay + 20);
  const ty = slideUp(frame, delay, delay + 20, 50);
  const metrics = [
    {label: "CAC", value: "???", color: T.accent},
    {label: "LTV", value: "???", color: T.accent},
    {label: "ROI", value: "???", color: T.accent},
  ];
  return (
    <div style={{opacity, transform: `translateY(${ty})`, marginTop: 16}}>
      <div style={{
        background: "#FFFFFF",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 12,
        padding: 24,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        maxWidth: CW,
      }}>
        {metrics.map((m, i) => {
          const md = delay + 20 + i * 6;
          return (
            <div key={i} style={{
              opacity: fadeIn(frame, md, md + 8),
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              background: `${m.color}10`,
              borderRadius: 8,
              border: `1px solid ${m.color}30`,
            }}>
              <span style={{fontFamily: FONT_SANS, fontSize: 14, fontWeight: 600, color: "#4A4A4A"}}>{m.label}</span>
              <span style={{fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 700, color: m.color}}>{m.value}</span>
            </div>
          );
        })}
        <div style={{
          opacity: fadeIn(frame, delay + 45, delay + 55),
          background: T.accent,
          borderRadius: 6,
          padding: "8px 16px",
          textAlign: "center",
          marginTop: 4,
        }}>
          <span style={{fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, color: "#FFFFFF", letterSpacing: 1}}>
            DADOS AUSENTES
          </span>
        </div>
      </div>
    </div>
  );
};

const ProfileCTA: React.FC<{delay?: number}> = ({delay = 0}) => {
  const frame = useCurrentFrame();
  const photoScale = interpolate(frame, [delay, delay + 15], [0.8, 1], {extrapolateRight: CLAMP, easing: Easing.out(Easing.ease)});
  const photoOp = fadeIn(frame, delay, delay + 15);
  return (
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 16}}>
      {/* Photo with rings */}
      <div style={{position: "relative", width: 100, height: 100, opacity: photoOp}}>
        {[3, 2, 1].map((ring) => (
          <div key={ring} style={{
            position: "absolute",
            inset: (ring - 1) * -20,
            borderRadius: "50%",
            border: `1px solid rgba(0,0,0,0.06)`,
            transform: `scale(${photoScale})`,
          }} />
        ))}
        <div style={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          overflow: "hidden",
          border: "2px solid rgba(0,0,0,0.1)",
          transform: `scale(${photoScale})`,
        }}>
          <Img
            src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}}
          />
        </div>
      </div>
      <Headline size={44} color={T.light.headline} delay={delay + 15}>Marketing, Dados & IA</Headline>
      <Body color={T.light.body} delay={delay + 25}><i>Siga para mais dicas.</i></Body>
      <Divider mode="light" delay={delay + 30} />
      <span style={{
        opacity: fadeIn(frame, delay + 30, delay + 40),
        fontFamily: FONT_SANS,
        fontSize: 15,
        color: T.light.muted,
      }}>@guilhermeribeiro.ao</span>
    </div>
  );
};

// ── SLIDE 1: HOOK ─────────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <GridPattern />
    <SeriesLabel mode="dark" text="SISTEMA DE MARKETING" />
    <Badge text="ALERTA DE SISTEMA" />
    <Headline size={50} color={T.dark.headline} delay={0}>
      5 sinais de que seu<br />marketing não é um sistema
    </Headline>
    <SlideNumber n="01" />
    <Footer mode="dark" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 2: DADO ÂNCORA ──────────────────────────────────────────────────
const S2: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <GridPattern />
    <SeriesLabel mode="dark" text="SISTEMA DE MARKETING" />
    <NumberHero value="83%" />
    <Body color={T.dark.body}>
      dos negócios que escalam usam sistemas de marketing documentados, não esforço individual.
    </Body>
    <Credit text="Análise Dimus, 2026" mode="dark" />
    <Footer mode="dark" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 3: SINAL 1 ──────────────────────────────────────────────────────
const S3: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <GridPattern />
    <SeriesLabel mode="dark" text="SISTEMA DE MARKETING" />
    <Headline size={46} color={T.dark.headline}>
      Se parar de postar,<br />para de vender
    </Headline>
    <Body color={T.dark.body} delay={10}>Dependência operacional total.</Body>
    <TerminalCard
      label="diagnóstico"
      delay={25}
      lines={[
        {type: "$", content: "marketing_status --check"},
        {type: "#", content: "# resultado atual"},
        {type: "text", content: "posts_semana: variável"},
        {type: "text", content: "resultado: imprevisível"},
        {type: "error", content: "sistema: AUSENTE ✗"},
        {type: "text", content: "> sem processo = sem escala"},
      ]}
    />
    <Footer mode="dark" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 4: SINAL 2 ──────────────────────────────────────────────────────
const S4: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.light.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <SeriesLabel mode="light" text="SISTEMA DE MARKETING" />
    <Headline size={46} color={T.light.headline}>
      Não sabe o CAC
    </Headline>
    <Body color={T.light.body} delay={10}>Se não mede, não controla.</Body>
    <DashboardCard delay={25} />
    <Footer mode="light" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 5: DADO CHAVE ───────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{backgroundColor: T.light.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
      <SeriesLabel mode="light" text="SISTEMA DE MARKETING" />
      <NumberHero value="72%" />
      <Body color={T.light.body}>
        do orçamento de{" "}
        <HighlightText color={T.accent}>tráfego é desperdiçado</HighlightText>
        {" "}sem funil estruturado.
      </Body>
      <Credit text="Benchmark Dimus, 2024-2026" mode="light" />
      <Footer mode="light" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
    </AbsoluteFill>
  );
};

// ── SLIDE 6: REVELAÇÃO ─────────────────────────────────────────────────────
const S6: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <GridPattern />
    <SeriesLabel mode="dark" text="SISTEMA DE MARKETING" />
    <Headline size={48} color={T.dark.headline}>O que mais falha</Headline>
    <Checklist
      items={[
        "Conteúdo sem linha editorial",
        "Funil que não retém leads",
        "CRM vazio ou ignorado",
        "Decisões por achismo, não dados",
      ]}
      mode="dark"
    />
    <Footer mode="dark" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 7: SINAL 5 ──────────────────────────────────────────────────────
const S7: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.dark.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <GridPattern />
    <SeriesLabel mode="dark" text="SISTEMA DE MARKETING" />
    <Headline size={44} color={T.dark.headline}>
      Sem arquitetura
    </Headline>
    <Body color={T.dark.body} delay={10}>Tráfego, conteúdo, funil, dados e IA desconectados.</Body>
    <TerminalCard
      label="sistema dimus"
      delay={25}
      lines={[
        {type: "$", content: "arquitetura --pilares"},
        {type: "#", content: "# 5_pilares_conectados"},
        {type: "arrow", content: "tráfego (aquisição)"},
        {type: "arrow", content: "conteúdo (autoridade)"},
        {type: "arrow", content: "funil (conversão)"},
        {type: "arrow", content: "dados (otimização)"},
        {type: "arrow", content: "IA (escala)"},
        {type: "success", content: "STATUS: INTEGRADO ✓"},
      ]}
    />
    <Footer mode="dark" handle="@guilhermeribeiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 8: RESOLUÇÃO ────────────────────────────────────────────────────
const S8: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.light.bg, justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <SeriesLabel mode="light" text="SISTEMA DE MARKETING" />
    <Headline size={42} color={T.light.headline}>
      Marketing previsível existe.<br />É sistema.
    </Headline>
    <Checklist
      items={[
        "Processo documentado e replicável",
        "Métricas claras (CAC, LTV, ROI)",
        "Funil automatizado com CRM",
        "IA como base operacional",
      ]}
      mode="light"
      delay={20}
    />
    <Footer mode="light" handle="@guilhermeriberiro.ao" author="Guilherme Ribeiro" />
  </AbsoluteFill>
);

// ── SLIDE 9: CTA ───────────────────────────────────────────────────────────
const S9: React.FC = () => (
  <AbsoluteFill style={{backgroundColor: T.light.bg, justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.sides}px ${SZ.bottom}px`}}>
    <SeriesLabel mode="light" text="SISTEMA DE MARKETING" />
    <ProfileCTA delay={0} />
  </AbsoluteFill>
);

// ── ROOT ──────────────────────────────────────────────────────────────────
const SLIDES = [
  {Component: S1, dur: 300},
  {Component: S2, dur: 300},
  {Component: S3, dur: 300},
  {Component: S4, dur: 300},
  {Component: S5, dur: 300},
  {Component: S6, dur: 300},
  {Component: S7, dur: 300},
  {Component: S8, dur: 300},
  {Component: S9, dur: 300},
];

export const CarrosselSistemaMarketing: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      {SLIDES.map(({Component, dur}, i) => (
        <Sequence key={i} from={offset} durationInFrames={dur}>
          <AbsoluteFill>
            <Component />
          </AbsoluteFill>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
